import styled, { useTheme } from 'styled-components'
import { useRef } from 'react'
import { useGetSize } from 'Hooks/useGetSize'
import * as d3 from 'd3'
import { AnimatedNote } from './AnimatedNote'
import { useMediaQuery } from 'styled-breakpoints/use-media-query'
import { Note, GridLine } from 'theme/styles'

const ExampleComponent = ({
  data,
  year,
  step,
}: {
  data: CsvRow[]
  year: number
  step: number
}) => {
  //const { breakpoints } = useTheme()
  //const isMobile = useMediaQuery(breakpoints.only('md'))

  const isMobile = window.innerWidth < 768
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const { width } = useGetSize(containerRef)
  const height = isMobile ? width * 1.2 : width * 0.6
  const selectedData = data.filter((d) => d.year === year)

  const maxX = d3.max(data, (d) => d.age) ?? 0
  const maxY = d3.max(data, (d) => d.countHigh) ?? 0

  const xDomain = [0, maxX]
  const yDomain = [0, maxY + maxY * 0.1]
  const margin = {
    top: 30,
    right: 0,
    bottom: 30,
    left: 80,
  }
  const tickYLength = 18
  const tickXLength = 12

  const xScale = d3
    .scaleLinear()
    .domain(xDomain)
    .range([margin.left, width - margin.right])

  const yScale = d3
    .scaleLinear()
    .domain(yDomain)
    .range([height - margin.bottom, margin.top])

  const lineGenerator = d3.line() //.curve(d3.curveCatmullRom.alpha(0.5))

  const ageDataLine: [number, number][] = selectedData.map((d) => {
    return [xScale(d.age), yScale(d.count)]
  })
  const ageDataHighLine: [number, number][] = selectedData.map((d) => {
    return [xScale(d.age), yScale(d.countHigh)]
  })

  const ageDataArea = [...ageDataLine]
  const ageDataHighArea = [...ageDataLine, ...ageDataHighLine.reverse()]

  ageDataArea.unshift([xScale(0), height - margin.bottom])
  ageDataArea.push([width - margin.right, height - margin.bottom])
  // ageDataHighArea.unshift([xScale(0), height - margin.bottom])
  // ageDataHighArea.push([width - margin.right, height - margin.bottom])

  const ageLinePath = lineGenerator(ageDataLine) || undefined
  const ageAreaPath = lineGenerator(ageDataArea) || undefined
  const ageAreaHighPath = lineGenerator(ageDataHighArea) || undefined

  const xTicks = isMobile
    ? [0, 25, 50, 75]
    : selectedData.filter((d) => d.age % 10 === 0).map((d) => d.age)
  const yTicks = isMobile
    ? step === 2
      ? [100000, 150000]
      : [50000, 100000, 150000]
    : [50000, 100000, 150000]

  return (
    <Container ref={containerRef}>
      <Svg ref={svgRef} width={width} height={height}>
        <linearGradient id='coloredGradient' x1='0' x2='0' y1='0' y2='1'>
          <stop offset='0%' stop-color='#ffe462ee' />
          <stop offset='90%' stop-color='#ffe46200' />
        </linearGradient>

        <linearGradient id='ghostGradient' x1='0' x2='0' y1='0' y2='1'>
          <stop offset='0%' stop-color='#f2f4f6' />
          <stop offset='100%' stop-color='#f2f4f666' />
        </linearGradient>

        {/* Y grid */}
        {/* {xTicks.map((value) => (
          <GridLine
            x1={xScale(value)}
            x2={xScale(value)}
            y1={margin.top}
            y2={height - margin.bottom}
          />
        ))} */}

        {/* X ticks */}
        {xTicks.map((value) => (
          <Tick
            x1={xScale(value)}
            x2={xScale(value)}
            y1={height - margin.bottom + tickXLength}
            y2={height - margin.bottom}
          />
        ))}

        {/* X grid */}
        {yTicks.map((value) => {
          return (
            <GridLine
              x1={0}
              x2={width - margin.right}
              y1={yScale(value)}
              y2={yScale(value)}
            />
          )
        })}

        {/* Y ticks */}
        {/* {yTicks.map((value) => {
          return (
            <Tick
              x1={margin.left - tickYLength - 20}
              x2={margin.left - 20}
              y1={yScale(value)}
              y2={yScale(value)}
            />
          )
        })} */}

        {step >= 6 && <Area highlight={step > 5} d={ageAreaHighPath} />}
        <Area highlight={step <= 5} d={ageAreaPath} />
        {step >= 6 && <AgePathLine d={ageLinePath} />}

        {step < 4 && (
          <OriginalPath
            highlight={true}
            d={
              lineGenerator([
                [margin.left, height - margin.bottom],
                ...data
                  .filter((d) => d.year === 2024)
                  .map<[number, number]>((d) => {
                    return [xScale(d.age), yScale(d.count)]
                  }),
                [width - margin.right, height - margin.bottom],
              ]) || undefined
            }
          />
        )}

        <Marker
          x1={xScale(65)}
          x2={xScale(65)}
          y1={margin.top}
          y2={height - margin.bottom}
        />

        <MarkerArea
          x={xScale(65)}
          y={margin.top}
          width={width - margin.right - xScale(65)}
          height={height - margin.bottom - margin.top}
        />

        {step === 3 && year > 1984 && year < 2030 && (
          <BabyBoomerArea
            x={xScale(year - 1946 - 18)}
            y={margin.top}
            width={xScale(18) - margin.left}
            height={height - margin.bottom - margin.top}
          />
        )}

        <BottomLine
          x1={0}
          x2={width - margin.right}
          y1={height - margin.bottom}
          y2={height - margin.bottom}
        />
      </Svg>

      {/* Y labels */}
      {yTicks.map((value) => (
        <Label
          style={{
            top: yScale(value) - 28,
            left: 0,
            transform: 'translateX(0)',
            textAlign: 'right',
            width: 50,
          }}
        >
          {value.toLocaleString('fr-CH')}
        </Label>
      ))}

      <Label
        style={{
          top: yScale(150000) - 48,
          left: 0,
          transform: 'translateX(0)',
          textAlign: 'left',
          letterSpacing: 0.85,
          width: 150,
        }}
      >
        <strong>Population</strong>
      </Label>

      {/* X labels */}
      {xTicks.map((value) => (
        <Label style={{ left: xScale(value), top: height }}>
          {value === 0 ? `0` : `${value} ans`}
        </Label>
      ))}

      {/* Animated line */}
      <AnimatedNote
        show={step === 3 && year > 1984 && year < 2030}
        position={[
          yScale(selectedData.find((d) => d.age === year - 1964)?.count ?? 0),
          xScale(year - 1964),
        ]}
      >
        La génération des <strong>baby-boomers</strong> se termine sur un pic
        de naissances en 1964.
      </AnimatedNote>

      {/* Animated line */}
      <AnimatedNote
        show={step === 4}
        position={[
          yScale(selectedData.find((d) => d.age === year - 1991)?.count ?? 0),
          xScale(year - 1991),
        ]}
      >
        <strong>Deuxième vague</strong> formée principalement par l'immigration
      </AnimatedNote>

      {/* Scenario high */}
      <AnimatedNote
        show={step >= 6 && year > 2024}
        position={[
          yScale(
            selectedData.find((d) => d.age === year - 2020)?.countHigh ?? 0
          ),
          xScale(year - 2020),
        ]}
      >
        Le <strong>scénario «haut»</strong> repose sur une hypothèse de taux de
        fécondité plus élevé.
      </AnimatedNote>

      {/* Line on 2024 for reference */}
      <AnimatedNote
        type='light'
        show={step < 2}
        top={false}
        position={[yScale(120000), xScale(30)]}
      >
        Répartition de la population <strong>en 2024</strong>
      </AnimatedNote>

      {/* 2nd World War */}
      <AnimatedNote
        show={step === 2}
        top={false}
        position={[
          yScale(selectedData.find((d) => d.age === year - 1917)?.count ?? 0),
          xScale(year - 1917),
        ]}
      >
        En 1950, la population de trentenaires était réduite, en partie à cause
        de la <strong>grippe espagnole</strong> qui avait réduit la fécondité
        trente ans auparavant.
      </AnimatedNote>

      {/* <MainNote
        style={{
          top: yScale(50000),
          left: xScale(30),
        }}
      >
        Répartition de la population <strong>en {year}</strong>
      </MainNote> */}

      <Note
        style={{
          top: margin.top + 20,
          left: xScale(65) + 10,
        }}
      >
        <strong>65 ans</strong>, âge de la retraite des femmes et des hommes en
        2028
      </Note>
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
  position: relative;
`

const AgePathLine = styled.path`
  stroke: ${({ theme }) => theme.backgroundColor};
  stroke-width: 1px;
  fill: none;
  opacity: 0.75;
`

const Line = styled.path`
  stroke-width: 3px;
  stroke: #ffe462;
  fill: none;
`

const Area = styled.path<{ highlight: boolean }>`
  stroke: none;
  fill: ${({ theme, highlight }) =>
    `rgb(${theme.mainColor}, ${highlight ? 1 : 0.5})`};
  transition: fill 0.3s;
`

const OriginalPath = styled(Area)`
  stroke: none;
  fill: ${({ theme }) =>
    theme.colorMode === 'light' ? '#22335511' : '#eeefff11'};
`

const Tick = styled.line`
  stroke: var(--text-color);
  stroke-width: 1.25px;
`

const Marker = styled.line`
  stroke: var(--text-color);
  stroke-width: 2px;
  stroke-dasharray: 2px 5px;
  stroke-linecap: round;
`

const MainNote = styled.div`
  width: 150px;
  letter-spacing: 0.05em;
  line-height: 1.2;
  text-align: left;
  position: absolute;
  // text-shadow:
  //   1px 1px 0 #ffffff88,
  //   -1px 1px 0 #ffffff88,
  //   -1px -1px 0 #ffffff88,
  //   1px -1px 0 #ffffff88;
`

const MarkerArea = styled.rect`
  fill: #202346;
  opacity: 0.05;
  stroke: none;
`

const BabyBoomerArea = styled.rect`
  fill: ${({ theme }) => `rgb(${theme.mainColor}, 0.2)`};
  stroke: none;
  mix-blend-mode: multiply;
`

const BottomLine = styled.line`
  stroke: var(--text-color);
  stroke-width: 1.25px;
`

const Label = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  color: var(--text-color);
  transform: translate(-50%, -50%);
  font-size: 0.8em;
`

const Svg = styled.svg``

export default ExampleComponent
